"use client";

import { useState, useEffect, useCallback } from "react";
import Image from "next/image";
import { Award, ShieldCheck, CheckCircle2, X, ChevronLeft, ChevronRight, FileText } from "lucide-react";
import type { LucideIcon } from "lucide-react";

interface SectionStyles {
  bg?: string;
  py?: number;
}

export interface CertItem {
  name: string;
  detail: string;
  images?: string[];
}

export interface CertificationsData {
  section_title?: string;
  items: CertItem[];
  styles?: SectionStyles;
}

const ICONS: LucideIcon[] = [Award, ShieldCheck, CheckCircle2, FileText];

function applySectionStyle(styles?: SectionStyles): React.CSSProperties {
  const s: React.CSSProperties = {};
  if (styles?.bg) s.backgroundColor = styles.bg;
  if (styles?.py != null) {
    s.paddingTop = `${styles.py}px`;
    s.paddingBottom = `${styles.py}px`;
  }
  return s;
}

// ── Lightbox ──────────────────────────────────────────────────────────────────

interface LightboxProps {
  images: string[];
  name: string;
  initialIndex: number;
  onClose: () => void;
}

function Lightbox({ images, name, initialIndex, onClose }: LightboxProps) {
  const [idx, setIdx] = useState(initialIndex);

  const prev = useCallback(() => setIdx((i) => (i - 1 + images.length) % images.length), [images.length]);
  const next = useCallback(() => setIdx((i) => (i + 1) % images.length), [images.length]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    }
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose, prev, next]);

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/85 flex flex-col items-center justify-center p-4"
      onClick={onClose}
    >
      {/* Header */}
      <div
        className="w-full max-w-3xl flex items-center justify-between mb-3 px-1"
        onClick={(e) => e.stopPropagation()}
      >
        <span className="text-white font-semibold text-sm truncate pr-4">{name}</span>
        <div className="flex items-center gap-3 shrink-0">
          <span className="text-blue-300 text-xs">
            {idx + 1} / {images.length}
          </span>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-white/15 hover:bg-white/30 flex items-center justify-center transition"
          >
            <X size={16} className="text-white" />
          </button>
        </div>
      </div>

      {/* Image */}
      <div
        className="relative w-full max-w-3xl flex-1 max-h-[76vh] rounded-xl overflow-hidden bg-white"
        onClick={(e) => e.stopPropagation()}
      >
        <Image
          src={images[idx]}
          alt={`${name} — trang ${idx + 1}`}
          fill
          className="object-contain"
          sizes="(max-width: 768px) 100vw, 800px"
          priority
        />
      </div>

      {/* Nav */}
      {images.length > 1 && (
        <div
          className="flex items-center gap-4 mt-4"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={prev}
            className="w-10 h-10 rounded-full bg-white/15 hover:bg-white/30 flex items-center justify-center transition"
          >
            <ChevronLeft size={20} className="text-white" />
          </button>

          {/* Dot strip (max 12 dots) */}
          <div className="flex gap-1.5">
            {images.slice(0, 12).map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                className={`w-2 h-2 rounded-full transition-all ${
                  i === idx ? "bg-white scale-125" : "bg-white/40 hover:bg-white/70"
                }`}
              />
            ))}
            {images.length > 12 && (
              <span className="text-white/50 text-xs self-center ml-1">+{images.length - 12}</span>
            )}
          </div>

          <button
            onClick={next}
            className="w-10 h-10 rounded-full bg-white/15 hover:bg-white/30 flex items-center justify-center transition"
          >
            <ChevronRight size={20} className="text-white" />
          </button>
        </div>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function CertificationsSection({ data }: { data: CertificationsData }) {
  const items = data.items || [];
  const [lightbox, setLightbox] = useState<{ item: CertItem; page: number } | null>(null);

  return (
    <section
      id="certifications"
      style={applySectionStyle(data.styles)}
      className="py-20 bg-white"
    >
      <div className="max-w-6xl mx-auto px-4">
        <p className="text-center text-sm font-medium text-[color:var(--brand-accent)] uppercase tracking-widest">
          Pháp lý & chất lượng
        </p>
        <h2 className="mt-2 text-3xl md:text-4xl font-bold text-center text-[color:var(--brand-dark)]">
          {data.section_title || "Chứng nhận & Giấy phép"}
        </h2>
        <p className="mt-3 text-center text-zinc-500 max-w-xl mx-auto">
          Bấm vào từng mục để xem hình ảnh giấy tờ đầy đủ.
        </p>

        <div className="mt-12 max-h-[560px] overflow-y-auto pr-1 rounded-xl grid sm:grid-cols-2 lg:grid-cols-3 gap-4 content-start [scrollbar-width:thin] [scrollbar-color:#bfdbfe_transparent]">
          {items.map((c, i) => {
            const IconComponent = ICONS[i % ICONS.length];
            const hasImages = c.images && c.images.length > 0;
            const thumb = hasImages ? c.images![0] : null;

            return (
              <div
                key={c.name}
                onClick={() => hasImages && setLightbox({ item: c, page: 0 })}
                className={`group rounded-2xl border border-blue-100 bg-[color:var(--bg-soft)] overflow-hidden transition-all duration-300 hover:border-[color:var(--brand)] hover:shadow-md ${
                  hasImages ? "cursor-pointer" : ""
                }`}
              >
                {/* Thumbnail */}
                {thumb && (
                  <div className="relative h-40 overflow-hidden bg-white border-b border-blue-100">
                    <Image
                      src={thumb}
                      alt={c.name}
                      fill
                      className="object-contain group-hover:scale-105 transition-transform duration-500"
                      sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                    />
                    {c.images && c.images.length > 1 && (
                      <span className="absolute bottom-2 right-2 bg-black/60 text-white text-xs px-2 py-0.5 rounded-full font-medium">
                        {c.images.length} trang
                      </span>
                    )}
                    <div className="absolute inset-0 bg-[color:var(--brand)]/0 group-hover:bg-[color:var(--brand)]/10 transition-colors flex items-center justify-center">
                      <span className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 text-[color:var(--brand)] text-xs font-semibold px-3 py-1.5 rounded-full shadow">
                        Xem chi tiết →
                      </span>
                    </div>
                  </div>
                )}

                {/* Info */}
                <div className="p-4 flex gap-3">
                  <div className="shrink-0 w-9 h-9 rounded-lg bg-white border border-blue-100 flex items-center justify-center shadow-sm mt-0.5">
                    <IconComponent size={18} className="text-[color:var(--brand)]" />
                  </div>
                  <div>
                    <div className="font-bold text-sm text-[color:var(--brand-dark)] leading-snug">
                      {c.name}
                    </div>
                    <p className="mt-1 text-xs text-zinc-500 leading-relaxed line-clamp-3">
                      {c.detail}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Lightbox */}
      {lightbox && (
        <Lightbox
          images={lightbox.item.images!}
          name={lightbox.item.name}
          initialIndex={lightbox.page}
          onClose={() => setLightbox(null)}
        />
      )}
    </section>
  );
}
